import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { OptOutItem } from '../types';

type OptOutManagerSliceState = {
  items: OptOutItem[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  selectedItems: string[]; // Array of values for selected items
};

const initialState: OptOutManagerSliceState = {
  items: [],
  totalCount: 0,
  loading: false,
  error: null,
  selectedItems: [],
};

export const optOutManagerSlice = createSlice({
  name: 'optOutManager',
  initialState,
  reducers: {
    reset: () => initialState,

    // Optimistic add: Add item immediately to state
    addOptOutItem: (state, action: PayloadAction<OptOutItem>) => {
      state.items.unshift(action.payload);
      state.totalCount += 1;
    },

    // Optimistic delete: Remove item immediately from state
    removeOptOutItem: (state, action: PayloadAction<string>) => {
      const index = state.items.findIndex((item) => item.value === action.payload);
      if (index !== -1) {
        state.items.splice(index, 1);
        state.totalCount -= 1;
      }
      // Also remove from selected items
      state.selectedItems = state.selectedItems.filter(
        (value) => value !== action.payload
      );
    },

    // Update optimistic item with real data from API response
    updateOptOutItem: (
      state,
      action: PayloadAction<{ oldValue: string; newItem: OptOutItem }>
    ) => {
      const index = state.items.findIndex(
        (item) => item.value === action.payload.oldValue
      );
      if (index !== -1) {
        state.items[index] = action.payload.newItem;
      }
    },

    // Set all items (for fresh data load)
    setOptOutItems: (state, action: PayloadAction<OptOutItem[]>) => {
      state.items = action.payload;
    },

    // Set total count
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },

    // Loading and error states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Selection management
    setSelectedItems: (state, action: PayloadAction<string[]>) => {
      state.selectedItems = action.payload;
    },

    toggleSelectedItem: (state, action: PayloadAction<string>) => {
      const value = action.payload;
      const index = state.selectedItems.indexOf(value);
      if (index !== -1) {
        state.selectedItems.splice(index, 1);
      } else {
        state.selectedItems.push(value);
      }
    },

    selectAllItems: (state, action: PayloadAction<string[]>) => {
      state.selectedItems = action.payload;
    },

    clearSelection: (state) => {
      state.selectedItems = [];
    },
  },
});

export const {
  reset,
  addOptOutItem,
  removeOptOutItem,
  updateOptOutItem,
  setOptOutItems,
  setTotalCount,
  setLoading,
  setError,
  setSelectedItems,
  toggleSelectedItem,
  selectAllItems,
  clearSelection,
} = optOutManagerSlice.actions;

export default optOutManagerSlice;
